import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Table, Pagination, Button, InputGroup, Toast, ToastContainer, Nav, Modal, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, onValue, set, remove, push } from 'firebase/database';
import { Car } from '../types/car';
import { Link, useLocation } from 'react-router-dom';

interface Fill {
  id: string;
  timestamp: number;
  odometer: number;
  fuelamount: number;
  price: number;
}

interface NewFill {
  odometer: string;
  fuelamount: string;
  price: string;
}

type SortField = 'timestamp' | 'odometer' | 'fuelamount' | 'price';
type SortDirection = 'asc' | 'desc';

const MoschtTankungen = () => {
  const { currentUser } = useAuth();
  const [ownedCarIds, setOwnedCarIds] = useState<string[]>([]);
  const [cars, setCars] = useState<Record<string, Car>>({});
  const [selectedCar, setSelectedCar] = useState<string>('');
  const [fills, setFills] = useState<Fill[]>([]);
  const [newFill, setNewFill] = useState<NewFill>({
    odometer: '',
    fuelamount: '',
    price: ''
  });
  const [showToast, setShowToast] = useState(false);
  const [lastConsumption, setLastConsumption] = useState<number | null>(null);
  const [sortField, setSortField] = useState<SortField>('odometer');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const location = useLocation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{type: 'success' | 'danger', message: string} | null>(null);

  useEffect(() => {
    if (!currentUser) return;

    // Fetch owned car IDs
    const ownedCarsRef = ref(database, `user_settings/${currentUser.uid}/ownedCars`);
    const ownedCarsUnsubscribe = onValue(ownedCarsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setOwnedCarIds(Object.values(data));
    });

    // Fetch all cars data
    const carsRef = ref(database, 'cars');
    const carsUnsubscribe = onValue(carsRef, (snapshot) => {
      const data = snapshot.val();
      setCars(data || {});
    });

    // Fetch selected car
    const selectedCarRef = ref(database, `user_settings/${currentUser.uid}/selectedCar`);
    const selectedCarUnsubscribe = onValue(selectedCarRef, (snapshot) => {
      const data = snapshot.val();
      setSelectedCar(data || '');
    });

    return () => {
      ownedCarsUnsubscribe();
      carsUnsubscribe();
      selectedCarUnsubscribe();
    };
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !selectedCar) {
      setFills([]);
      return;
    }

    const fillsRef = ref(database, `cars/${selectedCar}/fills`);
    const fillsUnsubscribe = onValue(fillsRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setFills([]);
        return;
      }

      const fillsList = Object.entries(data).map(([id, fill]: [string, any]) => ({
        id,
        ...fill,
        timestamp: Number(fill.timestamp),
        odometer: Number(fill.odometer),
        fuelamount: Number(fill.fuelamount),
        price: Number(fill.price)
      }));

      setFills(fillsList);
    });

    return () => fillsUnsubscribe();
  }, [currentUser, selectedCar]);

  const handleCarSelect = async (carId: string) => {
    if (!currentUser) return;
    
    try {
      const selectedCarRef = ref(database, `user_settings/${currentUser.uid}/selectedCar`);
      await set(selectedCarRef, carId);
      setSelectedCar(carId);
    } catch (error) {
      console.error('Error setting selected car:', error);
    }
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleRemoveFill = async (fillId: string) => {
    if (!currentUser || !selectedCar) return;
    
    if (!window.confirm('Möchten Sie diesen Eintrag wirklich löschen?')) {
      return;
    }

    try {
      const fillRef = ref(database, `cars/${selectedCar}/fills/${fillId}`);
      await remove(fillRef);
      setAlertMessage({
        type: 'success',
        message: 'Tankfüllung erfolgreich gelöscht!'
      });
      
      // Clear alert after 3 seconds
      setTimeout(() => {
        setAlertMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error removing fill:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Löschen der Tankfüllung. Bitte versuchen Sie es erneut.'
      });
    }
  };

  const calculateFuelConsumption = (currentOdometer: number, currentFuelAmount: number) => {
    // Sort fills by odometer in descending order
    const sortedFills = [...fills].sort((a, b) => b.odometer - a.odometer);
    
    // Find the previous fill (next highest odometer reading)
    const previousFill = sortedFills.find(fill => fill.odometer < currentOdometer);
    
    if (previousFill) {
      const distance = currentOdometer - previousFill.odometer;
      const consumption = (currentFuelAmount / distance) * 100; // L/100km
      return consumption;
    }
    
    return null;
  };

  const resetForm = () => {
    setNewFill({
      odometer: '',
      fuelamount: '',
      price: ''
    });
  };

  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) event.preventDefault();
    if (!currentUser || !selectedCar) return;

    try {
      const currentOdometer = Number(newFill.odometer);
      const currentFuelAmount = Number(newFill.fuelamount);

      // Create fill entry in database
      const fillsRef = ref(database, `cars/${selectedCar}/fills`);
      await push(fillsRef, {
        timestamp: Date.now(),
        odometer: currentOdometer,
        fuelamount: currentFuelAmount,
        price: Number(newFill.price)
      });

      // Calculate consumption
      const consumption = calculateFuelConsumption(currentOdometer, currentFuelAmount);
      setLastConsumption(consumption);

      // Reset form and close modal
      resetForm();
      setShowAddModal(false);
      
      // Show success toast
      setShowToast(true);
    } catch (error) {
      console.error('Error creating fill:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Speichern der Tankfüllung. Bitte versuchen Sie es erneut.'
      });
    }
  };

  const sortedFills = [...fills].sort((a, b) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    return (a[sortField] - b[sortField]) * multiplier;
  });

  const totalPages = Math.ceil(sortedFills.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedFills = sortedFills.slice(startIndex, startIndex + itemsPerPage);

  // Filter owned cars to only show active ones
  const activeCars = ownedCarIds
    .filter(id => cars[id] && cars[id].is_active)
    .map(id => ({
      id,
      ...cars[id]
    }));

  const renderSortArrow = (field: SortField) => {
    if (field !== sortField) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const renderPagination = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add page info text
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, sortedFills.length);
    
    items.push(
      <div key="info" className="d-flex align-items-center mb-2">
        <small className="text-muted">
          Zeige {startItem}-{endItem} von {sortedFills.length} Einträgen
        </small>
      </div>
    );

    // Add pagination controls
    const paginationItems = [];
    
    paginationItems.push(
      <Pagination.First 
        key="first" 
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(1)}
      />
    );
    paginationItems.push(
      <Pagination.Prev 
        key="prev" 
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(curr => curr - 1)}
      />
    );

    for (let number = startPage; number <= endPage; number++) {
      paginationItems.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    paginationItems.push(
      <Pagination.Next 
        key="next" 
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(curr => curr + 1)}
      />
    );
    paginationItems.push(
      <Pagination.Last 
        key="last" 
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(totalPages)}
      />
    );

    items.push(
      <Pagination className="mb-0 justify-content-center" key="pagination">
        {paginationItems}
      </Pagination>
    );

    return (
      <div className="mt-3">
        {items}
      </div>
    );
  };

  return (
    <Container>
      <ToastContainer 
        className="p-3" 
        position="top-end"
        style={{ zIndex: 1 }}
      >
        <Toast 
          onClose={() => setShowToast(false)} 
          show={showToast} 
          delay={6000} 
          autohide
          bg="success"
        >
          <Toast.Header closeButton={false}>
            <strong className="me-auto">Erfolg</strong>
          </Toast.Header>
          <Toast.Body className="text-white">
            Tankfüllung wurde erfolgreich gespeichert
            {lastConsumption !== null && (
              <div className="mt-1">
                Verbrauch: {lastConsumption.toFixed(1)} l/100km
              </div>
            )}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <h2 className="mb-4">Moscht Tankungen</h2>
      
      {/* Navigation Tabs */}
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht" 
            active={location.pathname === '/dashboard/moscht'}
          >
            Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/tankungen" 
            active={location.pathname === '/dashboard/moscht/tankungen'}
          >
            Tankungen
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/events" 
            active={location.pathname === '/dashboard/moscht/events'}
          >
            Events
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/logs" 
            active={location.pathname === '/dashboard/moscht/logs'}
          >
            Logs
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/cars" 
            active={location.pathname === '/dashboard/moscht/cars'}
          >
            Fahrzeuge
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/settings" 
            active={location.pathname === '/dashboard/moscht/settings'}
          >
            Konfiguration
          </Nav.Link>
        </Nav.Item>
      </Nav>
      
      {alertMessage && (
        <Alert variant={alertMessage.type} onClose={() => setAlertMessage(null)} dismissible className="mb-4">
          {alertMessage.message}
        </Alert>
      )}
      
      <div className="row mb-4">
        <Card className="mb-4">
          <Card.Body>
            <Card.Title style={{ color: '#1780a6' }}>Fahrzeugauswahl</Card.Title>
            <Form className="mt-3">
              <Form.Group>
                <Form.Label>Aktuelles Fahrzeug</Form.Label>
                <Form.Select 
                  value={selectedCar} 
                  onChange={(e) => handleCarSelect(e.target.value)}
                  className="mb-3"
                >
                  <option value="">Fahrzeug auswählen...</option>
                  {activeCars.map(car => (
                    <option key={car.id} value={car.id}>
                      {car.name}
                    </option>
                  ))}
                </Form.Select>
                {!activeCars.length && (
                  <div className="text-muted">
                    Keine aktiven Fahrzeuge verfügbar. Bitte fügen Sie zuerst Fahrzeuge in Ihrem Profil hinzu.
                  </div>
                )}
              </Form.Group>
            </Form>
          </Card.Body>
        </Card>

        {selectedCar && (
          <>
            <Card>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Card.Title style={{ color: '#1780a6' }}>Tankungen</Card.Title>
                  <Button 
                    variant="primary" 
                    onClick={() => {
                      resetForm();
                      setShowAddModal(true);
                    }}
                  >
                    Neue Tankfüllung erfassen
                  </Button>
                </div>
                <Table hover responsive>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('timestamp')} style={{ cursor: 'pointer' }}>
                        Datum{renderSortArrow('timestamp')}
                      </th>
                      <th onClick={() => handleSort('odometer')} style={{ cursor: 'pointer' }}>
                        Kilometerstand{renderSortArrow('odometer')}
                      </th>
                      <th onClick={() => handleSort('fuelamount')} style={{ cursor: 'pointer' }}>
                        Menge (l){renderSortArrow('fuelamount')}
                      </th>
                      <th onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}>
                        Preis (CHF){renderSortArrow('price')}
                      </th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedFills.map(fill => (
                      <tr key={fill.id}>
                        <td>
                          {new Intl.DateTimeFormat('de-DE', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          }).format(fill.timestamp)}
                        </td>
                        <td>{fill.odometer.toLocaleString('de-CH')}</td>
                        <td>{fill.fuelamount}</td>
                        <td>{fill.price}</td>
                        <td>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleRemoveFill(fill.id)}
                          >
                            Löschen
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {fills.length > itemsPerPage && renderPagination()}
                {!fills.length && (
                  <div className="text-muted">
                    Keine Tankfüllungen vorhanden.
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </div>

      {/* Add Fill Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Tankfüllung erfassen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Kilometerstand</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  value={newFill.odometer}
                  onChange={(e) => setNewFill({ ...newFill, odometer: e.target.value })}
                  min="0"
                  required
                />
                <InputGroup.Text>km</InputGroup.Text>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Getankte Menge</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  value={newFill.fuelamount}
                  onChange={(e) => setNewFill({ ...newFill, fuelamount: e.target.value })}
                  min="0"
                  step="0.01"
                  required
                />
                <InputGroup.Text>l</InputGroup.Text>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Preis</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  value={newFill.price}
                  onChange={(e) => setNewFill({ ...newFill, price: e.target.value })}
                  min="0"
                  step="0.01"
                  required
                />
                <InputGroup.Text>CHF</InputGroup.Text>
              </InputGroup>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Abbrechen
          </Button>
          <Button 
            variant="primary" 
            onClick={() => handleSubmit()}
          >
            Speichern
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MoschtTankungen;
